import { memo } from 'react'

import { ContentCopy, PhoneOutlined } from '@mui/icons-material'
import { IconButton, Link, Stack, Tooltip } from '@mui/material'
import { useSnackbar } from 'notistack'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'

export type PhoneLinkProps = {
  phoneNumber: string
}

/**
 * An on call number is read in an incident, so both things someone would do with it are one click:
 * dial it, or copy it into whatever they are actually paging from.
 */
const PhoneLinkRaw = ({ phoneNumber }: PhoneLinkProps) => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  // Numbers already on an account predate the E.164 field and may carry spacing that `tel:` chokes on.
  const dialable = phoneNumber.replace(/\s/g, '')

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
      <PhoneOutlined sx={{ fontSize: 14 }} />
      <Tooltip title={t('domain:Workspace.Event.OnCall.callPerson')} placement="top">
        <Link href={`tel:${dialable}`} variant="text-sm" underline="hover" noWrap>
          {phoneNumber}
        </Link>
      </Tooltip>
      <CopyToClipboard text={phoneNumber} onCopy={() => enqueueSnackbar(t('copiedToClipboard'), { variant: 'info' })}>
        <Tooltip title={t('clickToCopy')} placement="top">
          <IconButton size="small" aria-label={t('clickToCopy')}>
            <ContentCopy sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </CopyToClipboard>
    </Stack>
  )
}

export const PhoneLink = memo(PhoneLinkRaw)
